import React from "react";
import { useSelector } from "react-redux";
import { Box, Grid, Stack } from "@mui/material";
import VSNTypography from "../../../shared/VSNTypography";
import useOTBStyle from "../OTBStyle";
import GaugeChartComponent from "./GaugeChartComponent";
import ViolinBoxPlotChartComponent from "./ViolinBoxPlotChartComponent";

const HumanPerformanceAssessment = ({ data, pdf }) => {
  const classes = useOTBStyle();
  const OTBHPAReportData = useSelector(
    (state) => state?.api?.OTBReportData?.assessment_results
  );

  const calculateAverage = (arr) => {
    if (!Array.isArray(arr) || arr?.length === 0) return 0;

    const validNumbers = arr?.filter(
      (val) => val !== null && val !== undefined
    );
    return validNumbers?.length > 0
      ? (
          validNumbers?.reduce((sum, val) => sum + val, 0) /
          validNumbers?.length
        ).toFixed(1)
      : "0.0";
  };

  const AlcoholyData = data?.map((item) => item?.alcohol);
  const Alcoholranges = [
    { label: "Dependence", min: 0, max: 62.5, color: "#C83C32" },
    { label: "Hazardous", min: 62.6, max: 82.5, color: "#E8D0B1" },
    { label: "Low Risk", min: 82.5, max: 100, color: "#7CAB53" },
  ];

  const AnxietyyData = data?.map((item) => item?.anxiety);
  const Anxietyranges = [
    { label: "Severe", min: 0, max: 33.33, color: "#C83C32" },
    { label: "Moderate", min: 33.34, max: 57.14, color: "#F5B1AC" },
    { label: "Mild", min: 57.15, max: 80.95, color: "#E8D0B1" },
    { label: "Minimal", min: 80.96, max: 100, color: "#7CAB53" },
  ];

  const TraumaticStressyData = data?.map((item) => item?.traumatic_stress);
  const TraumaticStressranges = [
    { label: "Suggest PTSD", min: 0, max: 60, color: "#C83C32" },
    { label: "Unlikely PTSD", min: 61, max: 100, color: "#7CAB53" },
  ];

  const SecondaryStressyData = data?.map((item) => item?.secondary_stress);
  const SecondaryStressranges = [
    { label: "High", min: 0, max: 22.5, color: "#C83C32" },
    { label: "Average", min: 22.6, max: 70, color: "#E8D0B1" },
    { label: "Low", min: 70.1, max: 100, color: "#7CAB53" },
  ];

  const DepressionData = data?.map((item) => item?.depression);
  const Depressionranges = [
    { label: "Severe", min: 0, max: 29.63, color: "#C83C32" },
    { label: "Moderate Severe", min: 29.64, max: 48.15, color: "#F5B1AC" },
    { label: "Dependence", min: 48.16, max: 66.67, color: "#E8D0B1" },
    { label: "Hazardous", min: 66.68, max: 85.19, color: "#C9D9B5" },
    { label: "Low Risk", min: 85.2, max: 100, color: "#7CAB53" },
  ];

  const BurnoutData = data?.map((item) => item?.burnout);
  const Burnoutranges = [
    { label: "High", min: 0, max: 22.5, color: "#C83C32" },
    { label: "Average", min: 22.6, max: 70, color: "#E8D0B1" },
    { label: "Low", min: 71, max: 100, color: "#7CAB53" },
  ];

  const CompassionData = data?.map((item) => item?.compassion);
  const Compassionranges = [
    { label: "High", min: 0, max: 32.5, color: "#C83C32" },
    { label: "Average", min: 32.6, max: 77.5, color: "#E8D0B1" },
    { label: "Low", min: 77.6, max: 100, color: "#7CAB53" },
  ];

  const SleepData = data?.map((item) => item?.sleep_quality);
  const Sleepranges = [
    { label: "Severe", min: 0, max: 22, color: "#C83C32" },
    { label: "Moderate", min: 26, max: 50, color: "#F5B1AC" },
    { label: "Mild", min: 51, max: 75, color: "#E8D0B1" },
    { label: "Low", min: 76, max: 100, color: "#7CAB53" },
  ];

  const FamilyData = data?.map((item) => item?.family_social_support);
  const Familyranges = [
    { label: "High", min: 0, max: 34.72, color: "#C83C32" },
    { label: "Moderate", min: 34.73, max: 66.67, color: "#E8D0B1" },
    { label: "Low", min: 66.68, max: 100, color: "#7CAB53" },
  ];

  const FriendsData = data?.map((item) => item?.friend_social_support);
  const Friendsranges = [
    { label: "High", min: 0, max: 34.72, color: "#C83C32" },
    { label: "Moderate", min: 34.73, max: 66.67, color: "#E8D0B1" },
    { label: "Low", min: 66.68, max: 100, color: "#7CAB53" },
  ];

  const OthersData = data?.map((item) => item?.significant_social_support);
  const Othersranges = [
    { label: "High", min: 0, max: 34.72, color: "#C83C32" },
    { label: "Moderate", min: 34.73, max: 66.67, color: "#E8D0B1" },
    { label: "Low", min: 66.68, max: 100, color: "#7CAB53" },
  ];

  const ChartData = [
    {
      name: "Alcohol",
      value: calculateAverage(AlcoholyData),
      comparisonValue: OTBHPAReportData?.alcohol_consumption?.average_score,
      range: Alcoholranges,
    },
    {
      name: "Anxiety",
      value: calculateAverage(AnxietyyData),
      comparisonValue: OTBHPAReportData?.anxiety_severity?.average_score,
      range: Anxietyranges,
    },
    {
      name: "Compassion",
      value: calculateAverage(CompassionData),
      comparisonValue: OTBHPAReportData?.compassion?.average_score,
      range: Compassionranges,
    },
    {
      name: "Sleep Quality",
      value: calculateAverage(SleepData),
      comparisonValue: OTBHPAReportData?.sleep_quality?.average_score,
      range: Sleepranges,
    },
    {
      name: "Depression",
      value: calculateAverage(DepressionData),
      comparisonValue: OTBHPAReportData?.happiness_levels?.average_score,
      range: Depressionranges,
    },
    {
      name: "Burnout",
      value: calculateAverage(BurnoutData),
      comparisonValue: OTBHPAReportData?.burnout?.average_score,
      range: Burnoutranges,
    },
    {
      name: "Social Support",
      value: calculateAverage(OthersData),
      comparisonValue: OTBHPAReportData?.social_support?.average_score,
      range: Othersranges,
    },
    {
      name: "Traumatic Stress",
      value: calculateAverage(TraumaticStressyData),
      comparisonValue: OTBHPAReportData?.traumatic_stress?.average_score,
      range: TraumaticStressranges,
    },
    {
      name: "Secondary Stress",
      value: calculateAverage(SecondaryStressyData),
      comparisonValue: OTBHPAReportData?.secondary_stress?.average_score,
      range: SecondaryStressranges,
    },
  ];

  return (
    <>
      {pdf && (
        <Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always" }}
          >
            <VSNTypography variant="h4">
              Human Performance Assessment (HPA) Department vs Population
            </VSNTypography>

            <Stack direction="column" width="100%">
              <Stack direction="row" flexWrap="wrap">
                {ChartData?.map((item, index) => (
                  <Stack
                    key={index}
                    direction="column"
                    gap={2}
                    sx={{
                      width: {
                        xs: "22%",
                      },
                    }}
                  >
                    <GaugeChartComponent
                      name={item.name}
                      value={item.value}
                      otherValue={item.comparisonValue}
                      height="200px"
                      range={item?.range}
                      width="340px"
                    />
                  </Stack>
                ))}
              </Stack>
            </Stack>
            <VSNTypography
              sx={{
                marginTop: 1,
                fontSize: "14px",
                color: "#3A3A40",
                lineHeight: "20px",
                fontWeight: 400,
              }}
            >
              The golden gauge needle represents Department common score. The
              black line represents population common score in FLMH database.
            </VSNTypography>
          </Box>

          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always" }}
          >
            <VSNTypography variant="h4">
              Human Performance Assessment
            </VSNTypography>
            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  pdf
                  title="Alcohol"
                  yData={AlcoholyData}
                  ranges={Alcoholranges}
                  width="250px"
                  height="600px"
                  bodyPercentage={[-1, 101]}
                />
                <ViolinBoxPlotChartComponent
                  data={data}
                  pdf
                  title="Anxiety"
                  yData={AnxietyyData}
                  ranges={Anxietyranges}
                  width="250px"
                  height="600px"
                  bodyPercentage={[-1, 101]}
                />
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Traumatic Stress"
                  yData={TraumaticStressyData}
                  ranges={TraumaticStressranges}
                  pdf
                  width="250px"
                  height="600px"
                  bodyPercentage={[-1, 101]}
                />
                <ViolinBoxPlotChartComponent
                  data={data}
                  pdf
                  title="Secondary Stress"
                  yData={SecondaryStressyData}
                  ranges={SecondaryStressranges}
                  width="250px"
                  height="600px"
                  bodyPercentage={[-1, 101]}
                />
              </Stack>
            </Stack>
            <VSNTypography
              sx={{
                marginTop: 1,
                fontSize: "14px",
                color: "#3A3A40",
                lineHeight: "20px",
                fontWeight: 400,
              }}
            >
              Front Line DOES NOT diagnose or treat patients, data presented
              above represents symptoms suggestive of conditions.
            </VSNTypography>
          </Box>

          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always" }}
          >
            <VSNTypography variant="h4">
              Human Performance Assessment
            </VSNTypography>

            <Stack spacing={2}>
              <Stack direction="row" gap={2}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  pdf
                  title="Depression"
                  yData={DepressionData}
                  ranges={Depressionranges}
                  width="250px"
                  height="600px"
                  bodyPercentage={[-1, 101]}
                />
                <ViolinBoxPlotChartComponent
                  data={data}
                  pdf
                  title="Burnout"
                  yData={BurnoutData}
                  ranges={Burnoutranges}
                  width="250px"
                  height="600px"
                  bodyPercentage={[-1, 101]}
                />
                <ViolinBoxPlotChartComponent
                  data={data}
                  pdf
                  title="Compassion"
                  yData={CompassionData}
                  ranges={Compassionranges}
                  width="250px"
                  height="600px"
                  bodyPercentage={[-1, 101]}
                />

                <ViolinBoxPlotChartComponent
                  data={data}
                  pdf
                  title="Sleep (Insomnia)"
                  yData={SleepData}
                  ranges={Sleepranges}
                  width="250px"
                  height="600px"
                  bodyPercentage={[-1, 101]}
                />
              </Stack>
            </Stack>
            <VSNTypography
              sx={{
                marginTop: 1,
                fontSize: "14px",
                color: "#3A3A40",
                lineHeight: "20px",
                fontWeight: 400,
              }}
            >
              Front Line DOES NOT diagnose or treat patients, data presented
              above represents symptoms suggestive of conditions.
            </VSNTypography>
          </Box>

          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always" }}
          >
            <VSNTypography variant="h4">
              Human Performance Assessment
            </VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  pdf
                  title="Family Social Support"
                  yData={FamilyData}
                  ranges={Familyranges}
                  width="340px"
                  height="600px"
                  bodyPercentage={[-1, 101]}
                />
                <ViolinBoxPlotChartComponent
                  data={data}
                  pdf
                  title="Friends Social Support"
                  yData={FriendsData}
                  ranges={Friendsranges}
                  width="340px"
                  height="600px"
                  bodyPercentage={[-1, 101]}
                />
                <ViolinBoxPlotChartComponent
                  data={data}
                  pdf
                  title="Sig. Others Social Support"
                  yData={OthersData}
                  ranges={Othersranges}
                  width="340px"
                  height="600px"
                  bodyPercentage={[-1, 101]}
                />
              </Stack>
            </Stack>
            <VSNTypography
              sx={{
                marginTop: 1,
                fontSize: "14px",
                color: "#3A3A40",
                lineHeight: "20px",
                fontWeight: 400,
              }}
            >
              Front Line DOES NOT diagnose or treat patients, data presented
              above represents symptoms suggestive of conditions.
            </VSNTypography>
          </Box>
        </Box>
      )}

      {!pdf && (
        <Box>
          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                Human Performance Assessment (HPA) Department vs Population
              </VSNTypography>
            </div>
            <Grid container>
              {ChartData?.map((item, index) => (
                <Grid item key={index} xs={12} sm={6} md={3}>
                  <Box mx="auto" display="table" maxWidth={300} width="100%">
                    <GaugeChartComponent
                      name={item.name}
                      width="100%"
                      value={item.value}
                      otherValue={item.comparisonValue}
                      range={item?.range}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>

            <VSNTypography
              sx={{ fontSize: "16px", textAlign: "center", mt: 3 }}
            >
              The golden gauge needle represents Department common score. The
              black line represents population common score in FLMH database.
            </VSNTypography>
          </Box>

          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                Human Performance Assessment
              </VSNTypography>
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={3}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Alcohol"
                  yData={AlcoholyData}
                  ranges={Alcoholranges}
                  bodyPercentage={[-1, 101]}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Anxiety"
                  yData={AnxietyyData}
                  ranges={Anxietyranges}
                  bodyPercentage={[-1, 101]}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Traumatic Stress"
                  yData={TraumaticStressyData}
                  ranges={TraumaticStressranges}
                  bodyPercentage={[-1, 101]}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Secondary Stress"
                  yData={SecondaryStressyData}
                  ranges={SecondaryStressranges}
                  bodyPercentage={[-1, 101]}
                />
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                Human Performance Assessment
              </VSNTypography>
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={3}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Depression"
                  yData={DepressionData}
                  ranges={Depressionranges}
                  bodyPercentage={[-1, 101]}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Burnout"
                  yData={BurnoutData}
                  ranges={Burnoutranges}
                  bodyPercentage={[-1, 101]}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Compassion"
                  yData={CompassionData}
                  ranges={Compassionranges}
                  bodyPercentage={[-1, 101]}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Sleep (Insomnia)"
                  yData={SleepData}
                  ranges={Sleepranges}
                  bodyPercentage={[-1, 101]}
                />
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                Human Performance Assessment
              </VSNTypography>
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Family Social Support"
                  yData={FamilyData}
                  ranges={Familyranges}
                  bodyPercentage={[-1, 101]}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Friends Social Support"
                  yData={FriendsData}
                  ranges={Friendsranges}
                  bodyPercentage={[-1, 101]}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Sig. Others Social Support"
                  yData={OthersData}
                  ranges={Othersranges}
                  bodyPercentage={[-1, 101]}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
    </>
  );
};

export default HumanPerformanceAssessment;
